import React, { useEffect, useRef, useState } from "react";
import { Loader2, Play, Pause, Check, RotateCcw } from "lucide-react";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;

// "Does this sound like you?" — a short line in their clone, so a poor clone gets re-recorded before it's used.
export const SoundCheck = ({ onRedo, voiceId }) => {
  const key = `clone_ok_${voiceId || ""}`;
  const [ok, setOk] = useState(() => !!voiceId && localStorage.getItem(key) === "1");
  const [state, setState] = useState("idle");
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);

  const play = async () => {
    if (audio.current) { audio.current.paused ? audio.current.play() : audio.current.pause(); return; }
    setState("loading");
    // iPhone: start playback inside the tap, then swap in the real audio once it arrives.
    const a = new Audio("data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz280948NMBWInljyzsNRFLPWdnZGWrddDsjK1unuSrVN9jJsK8KuQtQCtMBjCEtImISdNKJOopIpBFpNSMbIHCSRpRR5iakjTiyzLhchUUBwCgyKiweBv/7UsQbg8isVNoMPMjAAAA0gAAABEVFGmgqK////9bP/6XCykxBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq");
    a.play().catch(() => {});
    try {
      const r = await fetch(`${API}/api/voice-clone/me/preview`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
      if (!r.ok) throw new Error();
      a.src = URL.createObjectURL(await r.blob());
      a.onplay = () => setState("playing");
      a.onpause = a.onended = () => setState("idle");
      audio.current = a;
      await a.play();
    } catch { setState("error"); }
  };
  const yes = () => { localStorage.setItem(key, "1"); setOk(true); };

  if (ok) {
    return <p className="text-xs flex items-center gap-2" style={{ color: TOK.creamMist, fontFamily: SANS }} data-testid="clone-soundcheck-ok"><Check size={12} style={{ color: TOK.gold }} /> You confirmed this sounds like you · <button type="button" onClick={() => setOk(false)} className="underline" data-testid="clone-soundcheck-reopen">listen again</button></p>;
  }
  return (
    <div className="rounded-2xl px-4 py-4 space-y-3" style={{ border: `1px solid ${TOK.goldMist}`, background: "rgba(245,236,217,0.03)" }} data-testid="clone-soundcheck">
      <p className="text-lg" style={{ fontFamily: SERIF, color: TOK.cream }}>Does this sound like you?</p>
      <button type="button" onClick={play} data-testid="clone-soundcheck-play" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.2em]" style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }}>
        {state === "loading" ? <Loader2 size={13} className="animate-spin" /> : state === "playing" ? <Pause size={13} /> : <Play size={13} />} Hear my voice
      </button>
      {state === "error" && <p className="text-xs" style={{ color: "#dc8b6c" }} data-testid="clone-soundcheck-error">We couldn't play it just now. Please try again.</p>}
      <div className="flex flex-wrap gap-3 pt-1">
        <button type="button" onClick={yes} data-testid="clone-soundcheck-yes" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em]" style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: SANS }}><Check size={12} /> Yes, that's me</button>
        <button type="button" onClick={onRedo} data-testid="clone-soundcheck-redo" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em]" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamMist, fontFamily: SANS }}><RotateCcw size={12} /> Not quite, record again (2 min)</button>
      </div>
    </div>
  );
};
